import {
    UIComponentCustomConfigRequest,
    UIComponentCustomConfigResponse
} from '@/api/services/common/ui-component/custom-config/ui-component-custom-config.dto'
import { uiComponentCustomConfigService } from '@/api/services/common/ui-component/custom-config/ui-component-custom-config.service'
import {
    UIComponentCustomFilterRequest,
    UIComponentCustomFilterResponse
} from '@/api/services/common/ui-component/custom-filter/ui-component-custom-filter.dto'
import { uiComponentCustomFilterService } from '@/api/services/common/ui-component/custom-filter/ui-component-custom-filter.service'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { VtCustomFilter } from '@/api/resources/common/shared/models/common.models'
import { ModalUtils } from '@/shared/util/modal-utils'
import i18n from '@/lang/i18n'

export class UiComponentUtils {
    // CUSTOM CONFIG ///

    static getCustomConfig<T>(
        componentCode: string,
        laboratoryId?: number
    ): Promise<UIComponentCustomConfigResponse<T> | null> | null {
        return PromiseUtils.toCallSecure(
            uiComponentCustomConfigService.get<T>(componentCode, laboratoryId),
            error => PromiseUtils.handleAxiosError(error, [404]))
    }

    private static _createCustomConfig<T>(
        componentCode: string,
        customConfig: UIComponentCustomConfigRequest<T>,
        laboratoryId?: number
    ): Promise<UIComponentCustomConfigResponse<T> | null> {
        return PromiseUtils.toCallSecure(
            uiComponentCustomConfigService.create<T>(
                componentCode,
                customConfig,
                laboratoryId))
    }

    private static _updateCustomConfig<T>(
        componentCode: string,
        componentCustomization: UIComponentCustomConfigRequest<T>,
        customConfigId: number,
        laboratoryId?: number
    ): Promise<UIComponentCustomConfigResponse<T> | null> {
        return PromiseUtils.toCallSecure(
            uiComponentCustomConfigService.update<T>(
                componentCode,
                componentCustomization,
                customConfigId,
                laboratoryId))
    }

    static async saveCustomConfig<T>(
        componentCode: string,
        customConfigRequest: UIComponentCustomConfigRequest<T>,
        laboratoryId?: number
    ): Promise<UIComponentCustomConfigResponse<T> | null> {
        const customConfigResponse = await UiComponentUtils.getCustomConfig<T>(componentCode, laboratoryId)
        if (customConfigResponse) {
            return UiComponentUtils._updateCustomConfig<T>(
                componentCode,
                customConfigRequest,
                customConfigResponse.id,
                laboratoryId)
        }
        return UiComponentUtils._createCustomConfig<T>(
            componentCode,
            customConfigRequest,
            laboratoryId)
    }

    /// CUSTOM FILTERS ///

    static getCustomFilter<T>(
        componentCode: string,
        laboratoryId?: number | undefined
    ): Promise<UIComponentCustomFilterResponse<T>[] | null> | null {
        return PromiseUtils.toCallSecure(
            uiComponentCustomFilterService.get<T>(componentCode, laboratoryId),
            error => PromiseUtils.handleAxiosError(error, [404]))
    }

    static createCustomFilter<T>(
        componentCode: string,
        customFilter: UIComponentCustomFilterRequest<T>,
        laboratoryId?: number
    ): Promise<UIComponentCustomFilterResponse<T> | null> {
        return PromiseUtils.toCallSecure(
            uiComponentCustomFilterService.create<T>(
                componentCode,
                customFilter,
                laboratoryId))
    }

    static removeCustomFilter(
        componentCode: string,
        customFilterId: number,
        laboratoryId?: number
    ): Promise<void | null> {
        return PromiseUtils.toCallSecure(
            uiComponentCustomFilterService.delete(
                componentCode,
                customFilterId,
                laboratoryId))
    }

    static updateCustomFilter<T>(
        componentCode: string,
        componentCustomization: UIComponentCustomFilterRequest<T>,
        customFilterId: number,
        laboratoryId?: number
    ): Promise<UIComponentCustomFilterResponse<T> | null> {
        return PromiseUtils.toCallSecure(
            uiComponentCustomFilterService.update<T>(
                componentCode,
                componentCustomization,
                customFilterId,
                laboratoryId))
    }

    static validateCustomFilter<T extends VtCustomFilter>(
        customFilter: UIComponentCustomFilterRequest<T> | UIComponentCustomFilterResponse<T>
    ): boolean {
        if (!customFilter.filterName) {
            ModalUtils.showWarningModal(i18n.tc('component.addFilterModal.noLabel'))
            return false
        } else if (!customFilter.filterCriteria.filterModel) {
            ModalUtils.showWarningModal(i18n.tc('component.addFilterModal.noFilter'))
            return false
        }
        return true
    }
}
